import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, AlertCircle, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { inventoryService } from '../services/dashboardService';
import { fieldClass, labelClass, parseNumber } from '../utils/form';
import { NumberField } from '../components/NumberField';
import { rowNumber } from '../utils/rowNumber';

type InventoryRow = {
  id: string;
  productName: string;
  sku: string;
  currentStock: number;
  reservedStock: number;
  sellableStock: number;
  totalSold: number;
  status: string;
};

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  const n = parseNumber(String(value ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

function mapInventoryItem(raw: Record<string, unknown>): InventoryRow {
  const product = (raw.product as Record<string, unknown> | undefined) ?? {};
  const currentStock = num(raw.currentStock ?? raw.stock);
  const reservedStock = num(raw.reservedStock ?? raw.reserved);
  const sellableStock = num(raw.sellableStock ?? raw.sellable, currentStock - reservedStock);
  const status =
    String(raw.status || '') ||
    (sellableStock <= 0 ? 'out_of_stock' : sellableStock <= 5 ? 'low_stock' : 'in_stock');

  return {
    id: String(raw.id ?? raw.productId ?? raw.sku ?? ''),
    productName: String(raw.productName ?? product.name ?? raw.name ?? '—'),
    sku: String(raw.sku ?? product.sku ?? ''),
    currentStock,
    reservedStock,
    sellableStock,
    totalSold: num(raw.totalSold ?? raw.sold),
    status,
  };
}

export function InventoryPage() {
  const [items, setItems] = useState<InventoryRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selected, setSelected] = useState<InventoryRow | null>(null);
  const [delta, setDelta] = useState('0');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryService.list();
      setItems((res.data ?? []).map((row) => mapInventoryItem(row)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت موجودی');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter(
      (item) => item.productName.includes(searchTerm) || item.sku.includes(searchTerm)
    );
  }, [items, searchTerm]);

  const openAdjust = (item: InventoryRow) => {
    setSelected(item);
    setDelta('0');
    setReason('');
    setAdjustOpen(true);
  };

  const closeAdjust = () => {
    setAdjustOpen(false);
    setSelected(null);
    setDelta('0');
    setReason('');
  };

  const handleAdjust = async () => {
    if (!selected) return;
    const deltaNum = parseNumber(delta);
    if (!Number.isFinite(deltaNum) || deltaNum === 0) {
      toast.error('مقدار تغییر باید عددی غیرصفر باشد');
      return;
    }
    if (!reason.trim()) {
      toast.error('دلیل تغییر الزامی است');
      return;
    }
    setSaving(true);
    try {
      await inventoryService.adjust(selected.id, deltaNum, reason.trim());
      toast.success('موجودی به‌روزرسانی شد');
      closeAdjust();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'تغییر موجودی ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">مدیریت انبار</h1>
          <p className="text-slate-600 mt-1">پیگیری و مدیریت موجودی محصولات</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="جستجو به نام یا SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
            dir="rtl"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">نام محصول</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">SKU</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">موجودی فعلی</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">رزرو شده</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">قابل فروش</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">کل فروخته شده</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                    موجودی‌ای یافت نشد
                  </td>
                </tr>
              ) : (
                filtered.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          📦
                        </div>
                        <p className="text-sm font-medium text-slate-900">{item.productName}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.sku}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.currentStock}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.reservedStock}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.sellableStock}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.totalSold}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full font-medium ${
                            item.status === 'in_stock'
                              ? 'bg-green-100 text-green-700'
                              : item.status === 'low_stock'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {item.status === 'in_stock' && '✓'}
                          {item.status === 'low_stock' && <AlertCircle className="h-3 w-3" />}
                          {item.status === 'in_stock'
                            ? 'فعال'
                            : item.status === 'low_stock'
                              ? 'کم‌موجودی'
                              : 'تمام شده'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => openAdjust(item)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium p-1"
                        title="تنظیم موجودی"
                      >
                        <Edit2 className="h-4 w-4" />
                        تنظیم موجودی
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={adjustOpen}
        onClose={closeAdjust}
        title="تنظیم موجودی"
        footer={
          <>
            <button
              type="button"
              onClick={closeAdjust}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleAdjust()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ثبت تغییر'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
            <p>
              <span className="text-slate-500">محصول: </span>
              <span className="font-medium text-slate-900">{selected?.productName}</span>
            </p>
            <p>
              <span className="text-slate-500">موجودی فعلی: </span>
              <span className="font-medium text-slate-900">{selected?.currentStock}</span>
            </p>
          </div>
          <NumberField
            label="تغییر موجودی (مثبت یا منفی)"
            value={delta}
            onChange={setDelta}
            allowNegative
            grouped={false}
            placeholder="مثلاً ۱۰ یا -۵"
          />
          <div>
            <label className={labelClass}>دلیل</label>
            <textarea
              className={fieldClass}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="دلیل تنظیم موجودی"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
